import React, { ReactElement } from 'react';
import {
  Component,
  Container,
  FORM_CONTROL,
  FilterCombo,
  Position,
  PuzzleButton,
  PuzzleCheckbox,
  PuzzleInput,
  PuzzleTagField,
  FieldWrap,
  Field,
  getUnixDate,
  noDataDropDownRender,
  sortByProperty,
  PuzzleTooltip,
  WithTooltip, PuzzleNumericField
} from "puzzle";
import { ComboBoxChangeEvent, ComboBoxFocusEvent, MultiSelectChangeEvent } from '@progress/kendo-react-dropdowns';
import {
  DETAILS_FORM_DATA,
  MSLA,
  POPUPDATA,
  PROJECT,
  PROJECT_MANAGER,
  RENDER_CONFIG_DATA,
  SOURCE_LANGUAGE,
  WORKFLOW
} from "../../Types";
import { DatePickerChangeEvent } from "@progress/kendo-react-dateinputs";
import _ from "lodash";
import Instructions from "../Instructions/Instructions";
import {
  TM_DATA,
  deleteFormControlParams,
  getFormattedDate,
  preventDisabledTagToOpen,
  targetLanguageItemColoring,
  tmTagRenderer
} from "../../../util/Util";
import { orderBy } from '@progress/kendo-data-query';
import Policies from "../../../util/Policies";
import { CheckboxChangeEvent } from "@progress/kendo-react-inputs";
import LocalizedDatePicker from "../../../util/LocalizedDatePicker/LocalizedDatePicker";
import { completedSubmissionStatuses } from "../../../util/Util";
import MSLAField from "./MSLA/MSLAField";
import PaClient from "./PaClient";
import PoliciesManager from "../../../util/Policies";
import classNames from "classnames";
import { MaskedTextBoxChangeEvent } from "@progress/kendo-react-inputs/dist/npm/maskedtextbox/MaskedTextBoxProps";

export type PROPS = {
  form: FORM_CONTROL,
  isTemplate?: boolean,
  projectPolicies: any,
  submissionMode: string,
  tptEnabled: boolean
};

export type LANGUAGE = {
  targetLanguage: string,
  targetLanguageDisplayName: string,
  default: boolean,
  hasTmConfig: boolean,
  hasGlossary: boolean
};

type QUOTE_CONFIG = {
  autoQuotesEnabled: boolean,
  projectQuoteCondition: string,
  quotablePhases: string[],
  requestQuoteDefault: boolean
};

export type CLAIM_SCOPE = {
  name: string,
  value: string
};

export type GENERAL_INFO_RESPONSE = {
  showClaimScope: boolean
};

export type STATE = {
  batchDueDate: number,
  claimScope: CLAIM_SCOPE,
  completedSubmission: boolean,
  isProjectDisabled: boolean,
  languageDirectionData: SOURCE_LANGUAGE[],
  mslaLevelData: MSLA[],
  originalWfName?: string,
  paClientName: string,
  pendingRequestQuote: boolean,
  projectData: PROJECT[],
  projectDataLoadInProgress: boolean,
  projectManagerData: PROJECT_MANAGER[],
  quoteConfig: QUOTE_CONFIG,
  requestQuoteDisableState: boolean,
  requestQuoteValue: boolean,
  selectedPms: PROJECT_MANAGER[],
  showClaim: boolean,
  showResetQuote: boolean,
  startedSubmission: boolean,
  submissionStatus: string,
  submissionMode: string,
  targetLanguageData: string[],
  templateDescription: string,
  templatesData: any[],
  workflowId: number,
  workflows: WORKFLOW[],
};

class Details extends Component<PROPS, STATE> {
  mandatoryQuoteStatuses = [
    'MANDATORY_APPROVAL_BEFORE_SUBMISSION_START',
    'MANDATORY_APPROVAL_BEFORE_CLAIM'
  ];
  claimLevelData = [
    {
      name: 'Batch',
      value: 'BATCH'
    },
    {
      name: 'Language',
      value: 'LANGUAGE'
    }
  ]
  quoteEnabled: boolean = true; // one of the values uses for quote request checkbox when cloning submission. In combination w/ pendingRequestQuote refers to checkbox value
  instructionsRef: any;
  submissionId: string = '';
  dueDateIgnoreField = false;
  static defaultProps = {
    isTemplate: false,
    submissionMode: 'CREATE'
  };

  public state: STATE = {
    batchDueDate: 0,
    claimScope: {
      name: '',
      value: ''
    },
    completedSubmission: false,
    isProjectDisabled: false,
    languageDirectionData: [],
    mslaLevelData: [],
    originalWfName: '',
    paClientName: '',
    pendingRequestQuote: false,
    projectData: [],
    projectDataLoadInProgress: false,
    projectManagerData: [],
    quoteConfig: {
      autoQuotesEnabled: false,
      projectQuoteCondition: '',
      quotablePhases: [],
      requestQuoteDefault: false
    },
    requestQuoteDisableState: true,
    requestQuoteValue: false,
    selectedPms: [],
    showClaim: false,
    showResetQuote: false,
    startedSubmission: false,
    submissionMode: '',
    submissionStatus: '',
    targetLanguageData: [],
    templateDescription: '',
    templatesData: [],
    workflowId: 0,
    workflows: []
  };

  detailsWrapperRef: any;
  tooltipRef: any;

  componentDidUpdate = (prevProps: PROPS) => {
    if (!_.isEqual(prevProps, this.props)) {
      this.setDueDateIgnoreField();
    }
  };

  /**
   * Changes isFavorite flag
   */
  changeFavoriteFlag = () => {
    let { isFavorite } = this.props.form.getFields();
    this.props.form.setData({
      isFavorite: !isFavorite.value
    });
  };

  /**
   * Workflow getter
   */
  getWorkflowData = () => {
    return this.state.workflows;
  };

  /**
   * Sets showClaim to true or false
   * @param response
   */
  setShowClaim = (response: GENERAL_INFO_RESPONSE) => {
    this.setState({ showClaim: response.showClaimScope });
  }

  /**
   * Return a workflow if it exists on a project
   */
  getWorkflowValue = (workflowId: number): WORKFLOW | undefined => {
    return _.find(this.state.workflows, { 'id': workflowId });
  };

  /**
   * Disables Details project combobox
   */
  disableProject = () => {
    this.setState({ isProjectDisabled: true });
  };

  /**
   * On date change all batches dates should be validated in following:
   * - if new due date is less then one of batch date, all dates (batch and phases) should be set to due date
   *
   * @param event
   */
  dueDateOnChange = (event: DatePickerChangeEvent) => {
    const { value } = event.target;
    this.dispatch('updateBatchDates', '', {
      submissionMode: this.props.submissionMode,
      date: getUnixDate(value)
    });
    this.props.form.handleChange(event);
    if (this.props.submissionMode === 'CLONE') {
      this.setState({
        batchDueDate: getUnixDate(value)
      }, () => {
        let { batchDueDate, workflowId } = this.state;
        if (batchDueDate && workflowId) {
          let params = { batchDueDate, workflowId };
          this.dispatch('getCalculatedDueDates', Position.end, {
            params,
            phasesModuleId: this.props.moduleId,
            simpleBatch: true
          })
        }
      })
    }
  };

  /**
   * Enables Details project combobox
   */
  enableProject = () => {
    if (this.props.submissionMode !== 'EDIT') {
      this.setState({ isProjectDisabled: false });
    }
  };

  /**
   * Find project by projectId
   *
   * @param projectId
   * @return project
   */
  findProjectById = (projectId: number): PROJECT | undefined => {
    const { projectData } = this.state;
    let projectDataLength = projectData.length;
    let current;
    let project;
    while (projectDataLength && !project) {
      projectDataLength--;
      current = projectData[projectDataLength];
      if (current.projectId === projectId) {
        project = current;
      }
    }

    return project;
  };

  /**
   * Get formData
   */
  getData = () => {
    let formData = this.props.form.getData();
    if (formData) {
      let {
        languageDirectionData,
        mslaLevelData,
        requestQuoteValue: requestQuote,
        templateDescription: description,
      } = this.state;
      let {
        isTemplate,
        tptEnabled
      } = this.props;

      formData = Object.assign(formData, {
        languageDirections: languageDirectionData[0].target,
        description,
        isTemplate,
        ...(!tptEnabled && { requestQuote })
      });

      if (!mslaLevelData.length) {
        delete formData.mslaLevel
      }
    }
    return formData;
  };

  /**
   * Gets default workflow from project if exists
   * @param projectId
   */
  getDefaultWorkflow = (projectId: number) => {
    let project = this.findProjectById(projectId);
    let defaultWorkflowId = project ? project.defaultWorkflowDefinitionId : null;
    return defaultWorkflowId;
  };

  /**
   * Returns
   * @param languageDirectionData
   */
  getDefaultTargetLanguages = (languageDirectionData: LANGUAGE[]) => {
    return languageDirectionData.filter((item: LANGUAGE) => item.default).map((item: LANGUAGE) => item.targetLanguage);
  };


  getProject = () => {
    const { project } = this.props.form.getData();
    return project;
  };

  /**
   * Recalculation of all flags needed for normal and Tpt mode render
   */
  getRenderConfiguration = (): RENDER_CONFIG_DATA => {
    const {
      projectManagerData,
      selectedPms,
      startedSubmission,
      submissionStatus,
      workflows
    } = this.state;

    const {
      form,
      submissionMode,
      projectPolicies
    } = this.props;

    const {
      getFields,
    } = form;

    const {
      workflow
    } = getFields();

    const editMode = submissionMode === "EDIT";
    const hasEditDueDatePolicy = projectPolicies?.hasEditDueDatePolicy;
    const hideDueDateField = !_.isEmpty(projectPolicies) ? _.includes(['CREATE', 'CLONE'], submissionMode) && !hasEditDueDatePolicy : false;

    let dueDateConditions = !projectPolicies?.hasForcedEditDueDatePolicy; //hasForcedEditDueDatePolicy always makes enables due date change
    if (dueDateConditions) {
      if (workflow.value) {
        dueDateConditions = workflows.every((item: any) => !((item.id === workflow.value) && (item.phases?.length === 0))); //if wf has no human steps enables due date change
      }
    }

    let disableDueDateByStatus = completedSubmissionStatuses.includes(submissionStatus) ? dueDateConditions : false;

    const disableDueDate = editMode && (!hasEditDueDatePolicy || disableDueDateByStatus);
    let pmData = [...projectManagerData];
    if (selectedPms.length && pmData.length) {
      let pmNotInProject = _.differenceBy(selectedPms, pmData, 'userId');
      if (pmNotInProject.length) {
        pmNotInProject = pmNotInProject.map((currentPm: PROJECT_MANAGER) => {
          return {
            ...currentPm,
            fullName: currentPm.firstName + ' ' + currentPm.lastName,
            notInProject: true
          };
        });
        pmData = pmData.concat(pmNotInProject)
      }
    }

    if (startedSubmission && workflows.length && workflow.value && !this.getWorkflowValue(workflow.value)) {
      this.updateWorkflowArray(workflow.value)
    }

    return {
      disableDueDate,
      editMode,
      hideDueDateField,
      pmData
    }
  };

  /**
   * Target languages getter
   */
  getTargetLanguage = () => this.state.targetLanguageData;

  /**
   * Returns default target language data
   */
  getDefaultTargetLanguagesData = () => {
    const { languageDirectionData } = this.state;
    const target: any = languageDirectionData[0].target;
    const defaultTargetLanguagesLocales: string[] = this.getDefaultTargetLanguages(target);
    return target.filter((currentTarget: any) => defaultTargetLanguagesLocales.includes(currentTarget.targetLanguage));
  };

  /**
   * Handle project change, dispatch action to get data by project
   */
  handleProjectChange = (event: ComboBoxChangeEvent) => {
    const projectId = event.target.value;
    let defaultWorkflowId = this.getDefaultWorkflow(projectId);
    this.props.form.removeAdditionallyAddedFields();
    this.dispatch('resetProjectRelatedDataAndSetNew', '', { projectId, defaultWorkflowId });

    const e = {
      ...event,
      target: {
        value: event.target.value,
        // @ts-ignore
        name: event.target.component.name
      }
    };
    this.props.form.handleChange(e);
    if (this.props.submissionMode === 'TEMPLATE_EDIT') {
      this.dispatch('templateEditValidateButtons', Position.end, { projectId: event.target.value })
    }
  };

  /**
   * Handle source language change. Find and set data for target language
   */
  handleSourceLanguageChange = (value: SOURCE_LANGUAGE | string, autoPopulated?: string) => {
    if (value) {
      let { languageDirectionData } = this.state;
      const { submissionMode } = this.props;
      let selectedItem: any = languageDirectionData.find((item) => {
        return item.source === value;
      });

      if (selectedItem) {
        const { target } = selectedItem;
        //has argument autoPopulated only when is called from setData() Dispatcher load flow.
        const defaultLanguages = autoPopulated ? [] : this.getDefaultTargetLanguages(target);
        // Removing value from targetLanguage field before setting new data, because it causes error if tries to select value that doesnt exist
        this.props.form.setData({ targetLanguage: defaultLanguages });

        let targetLanguageData = sortByProperty(target, 'targetLanguageDisplayName');

        let selectedTargetLanguages = targetLanguageData.filter((item: any) => defaultLanguages.includes(item.targetLanguage));
        this.dispatch('submissionTargetLanguageChange', '', { selectedTargetLanguages, submissionMode });

        this.setState({
          targetLanguageData: sortByProperty(target, 'targetLanguageDisplayName')
        });
      }
    } else {
      this.props.form.setData({ targetLanguage: [] });
    }
  };

  /**
   * Handle target language change, dispatch action to set reference files data
   */
  handleTargetLanguageChange = (event: MultiSelectChangeEvent) => {
    const { submissionMode } = this.props;
    let target = event.target;
    const targetLanguages = target.value;
    // @ts-ignore
    const translatedData = target.translatedData;

    let selectedTargetLanguages = translatedData.filter((item: any) => targetLanguages.includes(item.targetLanguage));

    this.dispatch('submissionTargetLanguageChange', '', { selectedTargetLanguages, submissionMode });

    this.props.form.handleChange(event);
  };

  /**
   * Handle template change
   */
  handleTemplateChange = (event: ComboBoxChangeEvent) => {
    let target = event.target;
    const templateId = target.value;
    const { template: oldTemplateId } = this.props.form.getData();
    let { submissionMode } = this.props;

    this.dispatch('confirmTemplateChange', '', { selectedTemplate: templateId, oldTemplateId, submissionMode });
  };

  /**
   * Returns value of hasRequestQuotePolicy
   */
  hasRequestQuotePolicy = () => {
    return Policies.hasRequestQuotePolicy;
  };

  /**
   * Target language field should be disabled when source language is invalid/empty
   */
  isTargetLanguageDisabled = () => {
    const { sourceLanguage } = this.props.form.getFields();
    return sourceLanguage.valid === false;
  };

  /**
   * Pa job change function
   * @param e
   */
  jobChange = (e: MaskedTextBoxChangeEvent) => {
    const {
      getFields,
      handleChange
    } = this.props.form;
    const { paClient } = getFields();
    handleChange(e);

    let { value = '' } = e;
    let paClientName = '';
    if (paClient.value) {
      paClientName = this.state.paClientName;
    }
    value = `${paClientName}-${value}`;

    const fakeEvent = {
      ...e,
      target: {
        ...e.target,
        name: 'submissionName'
      },
      value
    };

    handleChange(fakeEvent);
  };

  /**
   * Map project managers to add fullName
   */
  mapProjectManagersData = (projectManagerData: PROJECT_MANAGER[]) => {
    projectManagerData = projectManagerData.map((item: PROJECT_MANAGER) => {
      item.fullName = item.firstName + ' ' + item.lastName;
      return item;
    });

    return projectManagerData;
  };


  /**
   * On source language change call appropriate methods
   */
  onSourceLanguageChange = (event: ComboBoxChangeEvent) => {
    let value = event.target.value;

    this.handleSourceLanguageChange(value);

    this.props.form.handleChange(event);
  };

  /**
   * Open add template description dialog
   */
  openTemplateDescription = (e: any) => {
    e.preventDefault();
    let { templateDescription } = this.state;
    this.dispatch('openTemplateDescription', '', { templateDescription });
  };

  /**
   * Updates state with request quote checkbox change
   *
   * @param event
   */
  onRequestQuoteChange = (event: CheckboxChangeEvent) => {
    const { value: requestQuoteValue } = event;
    this.setState({
      requestQuoteValue
    })
  };

  /**
   * On workflow change.
   * Quote reset should be enabled in some conditions
   *
   * @param event
   */
  onWorkflowChange = (event: any) => {
    this.props.form.handleChange(event);
    if (event.value) {
      this.updateResetQuoteState(event.value);
      let workflowId = event.value.id;
      let { batchDueDate } = this.state;
      let params = { batchDueDate, workflowId };
      if (batchDueDate && workflowId) {
        this.dispatch('getCalculatedDueDates', Position.end, {
          params,
          phasesModuleId: this.props.moduleId,
          simpleBatch: true
        });
      }
      this.setState({
        workflowId
      }, () => {
        if (_.includes(['CLONE', 'EDIT', 'TEMPLATE_EDIT', 'TEMPLATE_CLONE'], this.props.submissionMode)) {
          this.dispatch('updateContentWorkflowInfo', '', {
            workflowData: {
              workflowId,
              workflowName: event.value.name
            }
          })
        }
      })
    } else {
      this.setState({ showResetQuote: false })
    }
  };

  /**
   * Pa client change function
   * @param e
   */
  paClientChange = (e: ComboBoxChangeEvent) => {
    const {
      getFields,
      setData
    } = this.props.form;
    const {
      job,
      project
    } = getFields();

    let { paClientName: submissionName = '' } = e.value || { paClientName: '' };
    submissionName = `${submissionName}-${job.value}`;

    let formData = {
      paClient: e.value,
      sourceLanguage: null,
      submissionName,
      targetLanguage: []
    };
    const stateData = {
      languageDirectionData: [],
      paClientName: e.value?.paClientName || ''
    };

    let getTptModeData = false;

    if (!!e.value) {
      getTptModeData = true;
      formData = {
        ...formData,
        paClient: e.value.paClientId
      };
    }

    setData(formData);
    this.setState(stateData, () => {
      if (getTptModeData) {
        let { paClientId } = e.value;
        this.dispatch('getTptModeLanguagesDirections', '', {
          paClientId,
          projectId: project.value
        });
      }
    });
  };

  /**
   * Used for selecting project when only one project exists
   * @param projectId
   */
  preselectProject = (projectId: number): void => {
    this.props.form.handleChange({
      target: {
        value: projectId,
        name: 'project'
      }
    });

    const {
      submissionMode,
      projectPolicies
    } = this.props;

    const hasEditDueDatePolicy = projectPolicies?.hasEditDueDatePolicy;

    const hideDueDateField = !_.isEmpty(projectPolicies) ? _.includes(['CREATE', 'CLONE'], submissionMode) && !hasEditDueDatePolicy : false;
    if (hideDueDateField) {
      this.props.form.handleChange({
        target: {
          value: null,
          name: 'specificDate'
        }
      });
    }
  };

  /**
   * Used for selecting sourceLanguage when only one project exists
   */
  preselectSourceLanguage = (): void => {
    const { languageDirectionData } = this.state;
    if (languageDirectionData.length === 1) {
      const source = languageDirectionData[0].source;
      this.props.form.handleChange({
        target: {
          value: source,
          name: 'sourceLanguage'
        }
      });
    }
    this.setState({
      targetLanguageData: sortByProperty(languageDirectionData[0].target, 'targetLanguageDisplayName')
    });
  };

  /**
   * Used for selecting targetLanguage if only one exists in sourceLanguage  exists
   */
  preselectTargetLanguage = (): void => {
    const { languageDirectionData } = this.state;
    const target: any = languageDirectionData[0].target;

    if (languageDirectionData.length === 1) {
      let targetLanguage = target.length === 1 ? [target[0].targetLanguage] : this.getDefaultTargetLanguages(target);
      this.props.form.handleChange({
        target: {
          value: targetLanguage,
          name: 'targetLanguage'
        }
      })
    }
  };

  /**
   * Resets back to false flag for loading project related data
   */
  projectDataLoadFinished = () => {
    this.setState({ projectDataLoadInProgress: false });
  };

  /**
   * Invalidates due date (if no valid value) on project focuses
   *
   * @param event
   */
  projectOnFocus = (event: ComboBoxFocusEvent) => {
    const { getFields } = this.props.form;
    if (!getFields().specificDate.value) {
      const e = {
        ...event,
        target: {
          value: null,
          name: 'specificDate'
        },
        value: null
      };
      this.props.form.handleChange(e);
    }
  };

  /**
   * Add targetLanguage to multi select data if it doesn't exist in it's dataItems
   * TODO: https://techqa1.translations.com/jira/browse/PDII-27123
   * This happens when you try to edit template that has been created with a target language
   * that has been removed from the project afterwards
   */
  addUnreachableTargetLanguages = (data: any): any => {
    const languageDirectionData: any = this.state.languageDirectionData;
    const batchInfosData: any = data.batchInfos[0].targetLanguageInfos;
    const targetLanguages = _.find(languageDirectionData, { source: data.sourceLanguage }).target;
    const dataLanguages = data?.targetLanguage;
    let matchingLanguage: any;
    let fakeLanguageData: any;

    dataLanguages.forEach((item: string) => {
      matchingLanguage = _.find(targetLanguages, (lang: any) => lang.targetLanguage === item);
      if (!matchingLanguage) {
        fakeLanguageData = _.find(batchInfosData, (lang: any) => lang.targetLanguage === item);
        targetLanguages.push({
          ...fakeLanguageData,
          notInProject: true
        });
      }
    });

    return languageDirectionData;
  };

  /**
   * Render for workflow section when is present and claim level and msla fields
   */
  workflowWithMslaAndClaimFieldsRender = () => {
    const {
      projectDataLoadInProgress,
      requestQuoteDisableState,
      requestQuoteValue,
      showResetQuote,
      startedSubmission,
      workflows
    } = this.state;
    const { form, submissionMode } = this.props;
    const { getFields, handleChange } = form;
    let {
      claimScope,
      isMultiBatch,
      workflow
    } = getFields();
    const workflowTooltip = this.getWorkflowValue(workflow.value)?.name;
    const editMode = submissionMode === "EDIT";
    const workflowTooltipMode = editMode && startedSubmission;

    deleteFormControlParams([workflow], projectDataLoadInProgress);

    return <>
      <FieldWrap
        layout={[9, 3]}
        withValidation
      >
        {!isMultiBatch?.value && (
          <Field
            fieldClassName={'pd-workflow-field'}
            fieldMandatory
          >
            <WithTooltip
              context={this}
              tooltipRef={'tooltipRef'}
              title={workflowTooltipMode ? workflowTooltip : ''}
              htmlTag={'span'}>
              <FilterCombo
                label={this.translate('workflow')}
                id={"workflow"}
                title={workflowTooltipMode ? workflowTooltip : ''}
                data={workflows}
                required={true}
                disabled={startedSubmission}
                name='workflow'
                textField='name'
                valueField="id"
                onChange={this.onWorkflowChange}
                handleFormChange={this.props.form.handleChange}
                listNoDataRender={() => noDataDropDownRender(this.translate('emptyWorkflow'))}
                translateMethod={this.translate.bind(this)}
                errorTxt={this.translateErrorMessage(workflow)}
                valueAsSimpleType={true}
                withValidation
                {...workflow}
              />
            </WithTooltip>
          </Field>)}
        {submissionMode !== 'EDIT' && showResetQuote &&
          <div className={'tp-request-quote-update-checkbox-wrap k-align-items-center k-hbox'}>
            <PuzzleCheckbox
              disabled={requestQuoteDisableState}
              className={`${requestQuoteValue ? 'qa-fakeClass-checked' : ''}`}
              name="requestQuote"
              id="requestQuote"
              label={this.translate('requestQuote')}
              value={showResetQuote && requestQuoteValue}
              onChange={this.onRequestQuoteChange}
            />
          </div>
        }
      </FieldWrap>
      <FieldWrap layout={[6, 6]} withValidation>
        <Field fieldMandatory>
          <FilterCombo
            data={this.claimLevelData}
            disabled={startedSubmission}
            errorTxt={this.translateErrorMessage(claimScope)}
            id={"claimScope"}
            label={this.translate('claimLevel')}
            listNoDataRender={() => noDataDropDownRender(this.translate('emptyClaimLevel'))}
            name={"claimScope"}
            onChange={handleChange}
            required={true}
            textField="name"
            translateMethod={this.translate.bind(this)}
            valueField="value"
            withValidation
            handleFormChange={handleChange}
            valueAsSimpleType={true}
            {...claimScope}
          />
        </Field>
        {this.mslaLevelRender()}
      </FieldWrap>
    </>
  }

  /**
   * Render for workflow section with all options
   */
  workflowRender = () => {
    const {
      mslaLevelData,
      projectDataLoadInProgress,
      requestQuoteDisableState,
      requestQuoteValue,
      showResetQuote,
      startedSubmission,
      workflows,
    } = this.state;
    const { form, submissionMode } = this.props;
    const { getFields, handleChange } = form;
    let { workflow, claimScope, isMultiBatch } = getFields();
    const workflowTooltip = this.getWorkflowValue(workflow.value)?.name;
    const editMode = submissionMode === "EDIT";
    const workflowTooltipMode = editMode && startedSubmission;

    deleteFormControlParams([workflow], projectDataLoadInProgress);

    return <>
      <FieldWrap
        className={'pd-workflow-wrap'}
        layout={[9, 3]}
        withValidation
      >
        {!isMultiBatch?.value && (
          <Field
            fieldMandatory
            fieldClassName={'pd-workflow-field'}
          >
            <WithTooltip
              context={this}
              tooltipRef={'tooltipRef'}
              title={workflowTooltipMode ? workflowTooltip : ''}
              htmlTag={'span'}>
              <FilterCombo
                label={this.translate('workflow')}
                id={"workflow"}
                title={workflowTooltipMode ? workflowTooltip : ''}
                data={workflows}
                required={true}
                disabled={startedSubmission}
                name='workflow'
                textField='name'
                valueField="id"
                onChange={this.onWorkflowChange}
                handleFormChange={this.props.form.handleChange}
                listNoDataRender={() => noDataDropDownRender(this.translate('emptyWorkflow'))}
                translateMethod={this.translate.bind(this)}
                errorTxt={this.translateErrorMessage(workflow)}
                valueAsSimpleType={true}
                withValidation
                {...workflow}
              />
            </WithTooltip>
          </Field>)}
        {submissionMode !== 'EDIT' && showResetQuote &&
          <div className={'tp-request-quote-update-checkbox-wrap k-align-items-center k-hbox'}>
            <PuzzleCheckbox
              disabled={requestQuoteDisableState}
              className={`${requestQuoteValue ? 'qa-fakeClass-checked' : ''}`}
              name="requestQuote"
              id="requestQuote"
              label={this.translate('requestQuote')}
              value={showResetQuote && requestQuoteValue}
              onChange={this.onRequestQuoteChange}
            />
          </div>
        }
        {this.state.showClaim &&
          <Field
            fieldClassName={'pd-claim-level-field'}
            fieldMandatory
          >
            <FilterCombo
              data={this.claimLevelData}
              disabled={startedSubmission}
              errorTxt={this.translateErrorMessage(claimScope)}
              id={"claimScope"}
              label={this.translate('claimLevel')}
              listNoDataRender={() => noDataDropDownRender(this.translate('emptyClaimLevel'))}
              name={"claimScope"}
              onChange={handleChange}
              required={true}
              textField="name"
              translateMethod={this.translate.bind(this)}
              valueField="value"
              withValidation
              handleFormChange={handleChange}
              valueAsSimpleType={true}
              {...claimScope}
            />
          </Field>}
        {mslaLevelData.length ? this.mslaLevelRender() : null}
      </FieldWrap>
    </>
  }

  /**
   * Reset project related data
   * @param projectId
   */
  resetProjectRelatedData = (projectId: number) => {
    const { form, tptEnabled, submissionMode } = this.props;
    let formData = {
      mediaAssetsNumber: '',
      projectManager: [],
      sourceLanguage: null,
      targetLanguage: [],
      workflow: null,
      ...(_.includes(['TEMPLATE_CREATE', 'TEMPLATE_EDIT'], submissionMode) &&
        { templateDueDateInDays: null }),
      ...(tptEnabled && {
        paClient: null
      })
    };
    form.setData(formData);

    let stateData: any = {
      languageDirectionData: [],
      mslaLevelData: [],
      projectManagerData: [],
      workflows: [],
      paClientName: '',
      requestQuoteValue: false,
      ...(projectId && { projectDataLoadInProgress: true })
    };

    this.setState(stateData)
  };

  /**
   * Set languages data, group by sourceLanguage
   */
  setLanguagesData = (languages: any) => {
    let parsedObj = {};

    let i = 0;
    let current;
    let currentTarget;
    let currentSource;
    let currentParsed;
    while (i < languages.length) {
      current = languages[i];
      currentTarget = current.targetLanguage;
      currentSource = current.sourceLanguage;
      // @ts-ignore
      currentParsed = parsedObj[currentSource];
      if (currentParsed) {
        // @ts-ignore
        parsedObj[currentSource].targetLanguages.push({
          targetLanguage: currentTarget,
          targetLanguageDisplayName: current.targetLanguageDisplayName,
          default: current.default,
          hasTmConfig: current.hasTmConfig,
          hasGlossary: current.hasGlossary,
          sourceLanguagePair: currentSource
        });
      } else {
        // @ts-ignore
        parsedObj[currentSource] = {
          sourceLanguageDisplayName: current.sourceLanguageDisplayName,
          targetLanguages: [{
            targetLanguage: currentTarget,
            targetLanguageDisplayName: current.targetLanguageDisplayName,
            default: current.default,
            hasTmConfig: current.hasTmConfig,
            hasGlossary: current.hasGlossary,
            sourceLanguagePair: currentSource
          }]
        }
      }
      i++;
    }

    let parsedArray = Object.keys(parsedObj).map((item) => {
      return {
        source: item,
        // @ts-ignore
        sourceLanguageDisplayName: parsedObj[item].sourceLanguageDisplayName,
        // @ts-ignore
        target: parsedObj[item].targetLanguages
      };
    });

    this.setState({
      languageDirectionData: sortByProperty(parsedArray, 'sourceLanguageDisplayName')
    });
  };

  /**
   * Set project
   */
  setProjectData = (projectData: PROJECT[]) => {
    this.setState({
      projectData: orderBy(projectData, [{
        field: 'name',
        dir: 'asc'
      }])
    })
  };

  /**
   * Sets msla request data to state, and if submission mode is create sets default msla level
   *
   * @param mslaLevelData
   */
  setPriorityLevelsData = (mslaLevelData: MSLA[] = []): void => {
    const {
      getFields,
      setFields
    } = this.props.form;

    if (!getFields().mslaLevel && mslaLevelData.length) {
      setFields({
        mslaLevel: {
          required: true,
          value: null,
          rules: []
        }
      });
    } else if (!mslaLevelData.length) {
      setFields({
        mslaLevel: {
          required: false,
          value: null,
          rules: []
        }
      });
    }

    this.setState({
      mslaLevelData,
    }, () => {
      const {
        form,
        submissionMode
      } = this.props;
      const defaultMslaId: number | undefined = _.find(mslaLevelData, { 'isDefaultMslaLevel': true })?.mslaLevelId;
      if (_.includes(['CREATE', 'TEMPLATE_CREATE'], submissionMode)) {
        form.setData({ mslaLevel: defaultMslaId });
      }
    });
  };

  /**
   *
   * Set project managers
   *
   * @param projectManagerData
   */
  setProjectManagers = (projectManagerData: PROJECT_MANAGER[]): void => {
    projectManagerData = this.mapProjectManagersData(projectManagerData);
    this.setState({
      projectManagerData
    });
  };

  /**
   * Set quote config data
   *
   * @param quoteConfig
   */
  setQuoteConfigData = (quoteConfig: QUOTE_CONFIG) => {
    this.setState({ quoteConfig });
  };

  /**
   * Set templates
   */
  setTemplatesData = (templatesData: any) => {
    this.setState({
      templatesData: templatesData.items
    })
  };

  /**
   * Set template description
   */
  setTemplateDescription = (templateDescription: string) => {
    this.setState({
      templateDescription
    });
  };

  /**
   * Automatically selects logged in user as project manager on project change
   */
  selectLoggedProjectManager = (userName: string) => {
    const { projectManagerData } = this.state;
    let length = projectManagerData.length;
    let pm = undefined;
    while (length-- && !pm) {
      if (projectManagerData[length].userName === userName) {
        pm = projectManagerData[length].userId;
      }
    }
    let formData = {
      projectManager: !pm ? [] : [pm],
      sourceLanguage: null,
      targetLanguage: []
    };
    this.props.form.setData(formData);
  };

  /**
   * Set template id
   * @param templateId
   */
  setTemplateId = (templateId: string) => {
    this.props.form.setData({
      template: templateId
    });
  };

  /**
   * Set data for editing
   */
  setData = (data: DETAILS_FORM_DATA, afterTemplateChange: boolean = false) => {
    this.quoteEnabled = data.quoteEnabled;
    const selectedPms: PROJECT_MANAGER[] = data.projectManager;
    // @ts-ignore
    data.projectManager = data.projectManager.map((item: PROJECT_MANAGER) => item.userId);
    const { submissionMode } = this.props;
    const languageDirectionData = this.addUnreachableTargetLanguages(data);

    if (data.sourceLanguage) {
      this.handleSourceLanguageChange(data.sourceLanguage, submissionMode);
    }

    let batchDueDate = data.batchInfos[0]?.targetLanguageInfos[0]?.dueDate;
    this.setState({
      batchDueDate,
      completedSubmission: data.completed,
      claimScope: data.claimScope,
      isProjectDisabled: submissionMode === 'EDIT',
      languageDirectionData: orderBy(languageDirectionData, [{
        field: 'sourceLanguageDisplayName',
        dir: 'asc'
      }]),
      originalWfName: data.batchInfos?.length === 1 ? data.batchInfos[0].workflowName : '',
      paClientName: data.projectAClientName,
      pendingRequestQuote: data.pendingRequestQuote,
      selectedPms,
      startedSubmission: data.started,
      submissionMode: submissionMode,
      submissionStatus: data.status,
      templateDescription: data.description
    });
    // setting data in form control must align with config provided otherwise bugs!
    let newData = _.omit(data, ['projectAClientName', 'completed', 'started', 'submissionMode', 'batchInfos', 'status', 'pendingRequestQuote']);
    let submissionData: any = _.cloneDeep(newData);
    let formData = this.props.form.getData();
    if (afterTemplateChange && formData.submissionName) {
      submissionData = _.omit(submissionData, 'submissionName')
    }

    if (!newData.mslaLevel && newData.project && this.isMslaEnabled(newData.project)) { //case when MSLA is added afterward to project
      let mslaLevel = _.find(this.state.mslaLevelData, { 'isDefaultMslaLevel': true })?.mslaLevelId as number;
      submissionData = {
        ...submissionData,
        mslaLevel
      }
    }
    this.props.form.setData(submissionData);
  };

  /**
   * Set due date ignore field
   */
  setDueDateIgnoreField = () => {
    const { startedSubmission } = this.state;
    const { form, submissionMode, projectPolicies } = this.props;
    const { ignoreFields } = form;
    const editMode = submissionMode === "EDIT";
    let ignore: string[] = [];
    const createMode = _.includes(['CREATE', 'CLONE'], submissionMode);
    let mslaLevel = form.getData().mslaLevel;
    if (projectPolicies && ((editMode && startedSubmission && projectPolicies.hasForcedEditDueDatePolicy !== true) || (createMode && !projectPolicies.hasEditDueDatePolicy) || (!startedSubmission && mslaLevel))) {
      ignore.push('specificDate');
      this.dueDateIgnoreField = true;
    } else {
      this.dueDateIgnoreField = false;
    }

    ignoreFields(ignore);
  }

  /**
   * Sets submission form date (specificDate)
   * @param date
   */
  setSpecificDate = (date: number | Date) => {
    if (typeof date === 'number') {
      date = new Date(date);
    }
    this.props.form.setData({
      specificDate: date
    })
  };

  /**
   * Set workflow data
   */
  setWorkflowData = (workflows: WORKFLOW[], defaultWorkflowId: number) => {
    const workflow = this.getWorkflowValue(defaultWorkflowId);
    this.setState({
      workflows: orderBy([...workflows], [{
        field: 'name',
        dir: 'asc'
      }])
    });
    if (workflow?.id && _.map(workflows, 'id').includes(workflow.id)) {
      this.props.form.setData({ workflow: workflow.id });
      this.updateResetQuoteState(workflow);
    }
  };

  /**
   * Set's data received form language popup
   * @param popupData
   */
  populateWithPopupData = (popupData: POPUPDATA) => {
    const { targetLanguage, sourceLanguage } = popupData;
    const detailsSourceLanguages = this.props.form.getData().sourceLanguage;

    if (detailsSourceLanguages !== sourceLanguage) {
      const { languageDirectionData } = this.state;
      const selectedItem: any = languageDirectionData.find((item) => {
        return item.source === sourceLanguage;
      });

      if (selectedItem) {
        const { target } = selectedItem;
        let targetLanguageData = sortByProperty(target, 'targetLanguageDisplayName');
        this.setState({
          targetLanguageData: sortByProperty(targetLanguageData, 'targetLanguageDisplayName')
        });
      }

      this.props.form.setData({
        targetLanguage,
        sourceLanguage
      });

    } else {
      this.props.form.setData({
        targetLanguage
      });
    }

  };

  /**
   * Sets value and disable state on request quote checkbox
   *
   * @param workflowDetails
   */
  updateResetQuoteState = (workflowDetails: any) => {
    const { submissionMode, quoteConfig, pendingRequestQuote } = this.state;
    const { quotablePhases, projectQuoteCondition, autoQuotesEnabled, requestQuoteDefault } = quoteConfig;
    const wFQuotablePhases: string[] = _.map(workflowDetails.phases, (phase: any) => phase.phaseName);
    const hasQuotablePhases = _.intersection(quotablePhases, wFQuotablePhases);
    if (hasQuotablePhases.length) {
      const mandatoryQuote = this.mandatoryQuoteStatuses.includes(projectQuoteCondition) || autoQuotesEnabled;
      let requestQuoteValue = mandatoryQuote || requestQuoteDefault;
      if (["TEMPLATE_EDIT", "CLONE"].includes(submissionMode)) { //If quote is mandatory on project level, request quote checkbox will be checked and grayed out, otherwise submission state
        if (!mandatoryQuote) {
          requestQuoteValue = pendingRequestQuote || this.quoteEnabled;
        }
      }
      this.setState({
        showResetQuote: true,
        requestQuoteDisableState: mandatoryQuote,
        requestQuoteValue: requestQuoteValue
      });
    } else {
      this.setState({ showResetQuote: false })
    }
  };

  /**
   * Only for started and one batch submissions
   * In case where wf is edited: There is wf with same name but different ID - covered in if condition
   * In case where wf is deleted: workflows array should be expanded with saved wf from batch
   *
   * @param workflowId
   */
  updateWorkflowArray = (workflowId: number) => {
    const { originalWfName = '', workflows } = this.state;
    const indexFound = _.findIndex(workflows, { 'name': originalWfName });
    if (indexFound > -1) {
      workflows[indexFound].id = workflowId;
    } else {
      workflows.push({
        description: null,
        id: workflowId,
        name: originalWfName
      })
    }
  };

  /**
   * Msla field render
   */
  mslaLevelRender = (): JSX.Element => {
    const {
      mslaLevelData,
      submissionStatus
    } = this.state;
    const { form } = this.props;
    const {
      getFields,
      handleChange
    } = form;
    const { mslaLevel, project } = getFields();

    return (
      <MSLAField
        {...{
          projectId: project?.value,
          mslaLevelData,
          handleChange,
          mslaLevel,
          submissionStatus
        }}
      />
    )
  };

  /**
   * Checks if project has msla enabled on it
   * @param projectId
   */
  isMslaEnabled = (projectId: number) => {
    let project = this.findProjectById(projectId);
    let mslaEnabled = project ? project.displayMslaLevel : false;
    return mslaEnabled;
  };

  /**
   * Returns layout config for TPT mode regarding active fields(Claim level, MSLA)
   */
  getTptModeLayout = () => {
    const {
      showClaim,
      mslaLevelData = []
    } = this.state;

    let layoutConfig = [3, 3, 6];
    if (!(showClaim || mslaLevelData.length)) {
      layoutConfig = [12];
    } else if ((!showClaim && mslaLevelData.length) || (showClaim && !mslaLevelData.length)) {
      layoutConfig = [3, 9];
    }

    return layoutConfig;
  }

  /**
   * Render dueInDays field
   * @param handleChange
   * @param templateDueDateInDays
   */
  dueInDaysFieldRender = (handleChange: Function, templateDueDateInDays: any) => {
    let projectId = this.props.form.getData('project')?.project;
    let showField = false;
    if (projectId) {
      showField = PoliciesManager.inProject(projectId).hasTemplateDueDateDays;
    }
    return (
      <>
        {showField &&
          <Field fieldClassName={'tp-due-in-days'}>
            <PuzzleNumericField
              format={'n1'}
              id='dueInDays'
              label={this.translate('dueInDays')}
              name='templateDueDateInDays'
              min={0.5}
              onChange={handleChange}
              spinners={false}
              withTooltip
              {...templateDueDateInDays}
            />
            <WithTooltip
              context={this}
              tooltipRef={'tooltipRef'}
              title={this.translate('dueInDaysTooltip')}
              htmlTag={'span'}>
              <i className={'tp-icon tp-info tp-color-accent'} />
            </WithTooltip>
          </Field>
        }
      </>
    )


  }

  /**
   * Render that will be used if instance is set to tptEnabled(tpt mode)
   */
  tptModeRender = () => {
    const {
      completedSubmission,
      mslaLevelData,
      projectData,
      startedSubmission,
      templateDescription,
      workflows
    } = this.state;

    const {
      form,
      isTemplate,
      submissionMode,
    } = this.props;

    const {
      getFields,
      handleChange,
      handleBlur
    } = form;

    const {
      claimScope,
      isFavorite,
      isMultiBatch,
      job,
      paClient,
      project,
      projectManager,
      specificDate,
      submissionName,
      templateDueDateInDays,
      workflow
    } = getFields();

    const {
      disableDueDate,
      editMode,
      hideDueDateField,
      pmData
    } = this.getRenderConfiguration();

    const workflowTooltipMode = editMode && startedSubmission;
    const workflowTooltip = this.getWorkflowValue(workflow.value)?.name;
    let specificDateField = this.dueDateIgnoreField ? _.omit(specificDate, 'errorTxt') : specificDate;
    return (
      <>
        <FieldWrap
          withValidation
          className={`${isTemplate ? 'tp-template-name-field-wrap' : 'tp-9-3'}`}
        >
          <Field
            fieldWithAdditionalEl
          >
            <PuzzleInput
              className={'tp-100'}
              label={isTemplate ? this.translate('templateName') : this.translate('submissionName')}
              id='submissionName'
              name='submissionName'
              fieldMandatory
              onChange={handleChange}
              onBlur={handleBlur}
              errorTxt={this.translateErrorMessage(submissionName)}
              withValidation
              {...submissionName}
            />
            {
              isTemplate ?
                submissionMode === 'TEMPLATE_EDIT' ?
                  <>
                    {this.dueInDaysFieldRender(handleChange, templateDueDateInDays)}
                    <PuzzleButton
                      style={{ marginTop: '4px', marginLeft: '10px' }}
                      aria-label={this.translate("SRL-openTemplateDetails")}
                      titleText={this.translate("templateDescriptionTooltip")}
                      iconClass={'tp-icon tp-info'}
                      fillMode="flat"
                      onClick={this.openTemplateDescription}
                      tooltipPosition={'right'}
                      withTooltip
                    />
                  </>
                  :
                  <>
                    {this.dueInDaysFieldRender(handleChange, templateDueDateInDays)}
                    <PuzzleButton
                      style={{ marginTop: '4px', marginLeft: '10px' }}
                      onClick={this.openTemplateDescription}
                      iconClass={`tp-icon ${templateDescription ? 'tp-edit' : 'tp-add'}`}
                      themeColor={'primary'}
                      fillMode="flat"
                    >
                      {templateDescription ? this.translate('editDescriptions') : this.translate('addDescriptions')}
                    </PuzzleButton></> :
                <div className={'tp-star-container'}>
                  <PuzzleCheckbox
                    titleText={this.translate('SRL-favoriteSub')}
                    checked={isFavorite.value}
                    className={`tp-checkbox-favorite ${isFavorite.value ? 'qa-fakeClass-checked' : ''}`}
                    aria-label={this.translate('SRL-favoriteSub')}
                    onChange={this.changeFavoriteFlag}
                    tooltipPosition={'right'}
                    withTooltip
                  />
                </div>
            }
          </Field>
          {
            isTemplate ?
              null :
              <Field
                fieldMandatory
                fieldClassName={'tp-date-field'}
              >
                {!hideDueDateField && <>
                  <LocalizedDatePicker
                    errorTxt={this.translateErrorMessage(specificDate)}
                    format={this.translateByNamespace('date.datePickerFormat')}
                    formatPlaceholder={getFormattedDate(this.translate)}
                    id={"dueDate"}
                    label={this.translate('dueDate')}
                    min={new Date()}
                    name='specificDate'
                    onChange={this.dueDateOnChange}
                    required={true}
                    valid={specificDate?.valid !== false}
                    withValidation
                    {...specificDateField}
                    disabled={disableDueDate}
                    valueAsSimpleType={true}
                  />
                </>}
              </Field>
          }
        </FieldWrap>
        <FieldWrap
          layout={[6, 6]}
          withValidation
        >
          <Field fieldMandatory>
            <FilterCombo
              aria-label={this.translate('department')}
              data={projectData}
              disabled={this.state.isProjectDisabled}
              errorTxt={this.translateErrorMessage(project)}
              handleFormChange={this.props.form.handleChange}
              id={'project'}
              label={this.translate('department')}
              name='project'
              onChange={this.handleProjectChange}
              onFocus={this.projectOnFocus}
              required={true}
              textField='name'
              translateMethod={this.translate.bind(this)}
              valueAsSimpleType={true}
              valueField="projectId"
              withValidation
              {...project}
            />
          </Field>
          <Field fieldMandatory>
            <PaClient
              errorTxt={this.translateErrorMessage(paClient)}
              onChange={this.paClientChange}
              projectId={project.value}
              required={true}
              startedSubmission={startedSubmission}
              withValidation
              {...paClient}
            />
          </Field>
        </FieldWrap>
        <FieldWrap
          className={'pd-workflow-wrap'}
          layout={[3, 9]}
          withValidation
        >
          <PuzzleInput
            asField
            fieldMandatory
            label={this.translate('job')}
            id='job'
            name='job'
            onChange={this.jobChange}
            onBlur={handleBlur}
            errorTxt={this.translateErrorMessage(job)}
            withValidation
            {...job}
          />
          {!isMultiBatch?.value && (
            <Field
              fieldMandatory
              fieldClassName={'pd-workflow-field'}
            >
              <WithTooltip
                context={this}
                tooltipRef={'tooltipRef'}
                title={workflowTooltipMode ? workflowTooltip : ''}
                htmlTag={'span'}>
                <FilterCombo
                  label={this.translate('workflow')}
                  id={"workflow"}
                  title={workflowTooltipMode ? workflowTooltip : ''}
                  data={workflows}
                  required={true}
                  disabled={startedSubmission}
                  name='workflow'
                  textField='name'
                  valueField="id"
                  onChange={this.onWorkflowChange}
                  handleFormChange={this.props.form.handleChange}
                  listNoDataRender={() => noDataDropDownRender(this.translate('emptyWorkflow'))}
                  translateMethod={this.translate.bind(this)}
                  errorTxt={this.translateErrorMessage(workflow)}
                  valueAsSimpleType={true}
                  withValidation
                  {...workflow}
                />
              </WithTooltip>
            </Field>)}
        </FieldWrap>
        <FieldWrap
          className={'pd-details-tptMode-field-wrap'}
          layout={this.getTptModeLayout()}
          withValidation
        >
          {this.state.showClaim &&
            <Field
              fieldClassName={'pd-claim-level-field'}
              fieldMandatory
            >
              <FilterCombo
                data={this.claimLevelData}
                disabled={startedSubmission}
                errorTxt={this.translateErrorMessage(claimScope)}
                id={"claimScope"}
                label={this.translate('claimLevel')}
                name={"claimScope"}
                onChange={handleChange}
                required={true}
                textField="name"
                translateMethod={this.translate.bind(this)}
                valueField="value"
                withValidation
                handleFormChange={handleChange}
                valueAsSimpleType={true}
                {...claimScope}
              />
            </Field>
          }
          {mslaLevelData.length ? this.mslaLevelRender() : null}
          <Field fieldMandatory>
            <PuzzleTagField
              aria-label={'SelectProjectManager'}
              autoClose={false}
              data={orderBy(pmData, [{
                field: 'fullName',
                dir: 'asc'
              }])}
              disabled={completedSubmission}
              errorTxt={this.translateErrorMessage(projectManager)}
              id={"projectManager"}
              label={this.translate('projectManager')}
              listNoDataRender={() => noDataDropDownRender(this.translate('emptyProjectManager'))}
              name='projectManager'
              onChange={handleChange}
              required={true}
              textField={'fullName'}
              valueField={'userId'}
              withValidation
              {...projectManager}
            />
          </Field>
        </FieldWrap>
      </>
    )
  };

  /**
   * @inheritDoc
   */
  renderBody() {
    const {
      completedSubmission,
      languageDirectionData,
      mslaLevelData,
      projectData,
      projectDataLoadInProgress,
      startedSubmission,
      submissionStatus,
      targetLanguageData,
      templateDescription
    } = this.state;
    const {
      form,
      isTemplate,
      submissionMode,
      tptEnabled
    } = this.props;
    const { getFields, handleChange, handleBlur } = form;
    let {
      isFavorite,
      isMultiBatch,
      paClient = { value: undefined },
      project,
      projectManager,
      sourceLanguage,
      specificDate,
      submissionName,
      targetLanguage,
      templateDueDateInDays
    } = getFields();

    deleteFormControlParams([projectManager, sourceLanguage, targetLanguage], projectDataLoadInProgress);

    const {
      disableDueDate,
      hideDueDateField,
      pmData
    } = this.getRenderConfiguration();
    let projectId = this.props.form.getData('project')?.project;
    let showTemplateDueInDays = false;
    if (projectId) {
      showTemplateDueInDays = PoliciesManager.inProject(projectId).hasTemplateDueDateDays;
    }
    const templateWrapCls = classNames('', {
        'tp-template-name-field-wrap': isTemplate,
        'tp-9-3': !isTemplate,
        'tp-with-due-in-days': isTemplate && showTemplateDueInDays
      }
    )
    let specificDateField = this.dueDateIgnoreField ? _.omit(specificDate, 'errorTxt') : specificDate;
    return (
      <Container
        className='tp-left-section tp-details-section'
        ref={(wrapper: any) => this.detailsWrapperRef = wrapper}
      >
        <Container
          className={'tp-section-container'}
          hBox
        >
          <Container
            className={'tp-section-shadow'}
            flex1
          >
            <Container
              className={'tp-section-container-content'}
              vBox
            >
              <Container
                ariaLabel={this.translate('SRL-section')}
                className={'tp-details-first-section'}
                htmlTag={'section'}
                tabIndex={0}
              >
                <Container
                  htmlTag={'h3'}
                  hBox
                  alignItemsCenter
                  justifyContentBetween
                  className="tp-section-container-title tp-100 tp-font-size-md tp-font-size-md"
                >
                  {this.translate('title')}
                </Container>
                {!tptEnabled ? (<form className={'k-form'}>
                  <FieldWrap
                    withValidation
                    className={templateWrapCls}
                  >
                    <Field
                      fieldWithAdditionalEl
                    >
                      <PuzzleInput
                        fieldMandatory
                        className={'tp-100'}
                        label={isTemplate ? this.translate('templateName') : this.translate('submissionName')}
                        id='submissionName'
                        name='submissionName'
                        onChange={handleChange}
                        onBlur={handleBlur}
                        errorTxt={this.translateErrorMessage(submissionName)}
                        withValidation
                        {...submissionName}
                      />
                      {
                        isTemplate ?
                          submissionMode === 'TEMPLATE_EDIT' ?
                            <>
                              {this.dueInDaysFieldRender(handleChange, templateDueDateInDays)}
                              <PuzzleButton
                                className={'tp-description-btn'}
                                aria-label={this.translate("SRL-openTemplateDetails")}
                                titleText={this.translate("templateDescriptionTooltip")}
                                iconClass={'tp-icon tp-info'}
                                fillMode="flat"
                                onClick={this.openTemplateDescription}
                                tooltipPosition={'right'}
                                withTooltip
                              />
                            </>
                            :
                            <>
                              {this.dueInDaysFieldRender(handleChange, templateDueDateInDays)}
                              <PuzzleButton
                                className={'tp-description-btn'}
                                onClick={this.openTemplateDescription}
                                iconClass={`tp-icon ${templateDescription ? 'tp-edit' : 'tp-add'}`}
                                themeColor={'primary'}
                                fillMode="flat"
                              >
                                {templateDescription ? this.translate('editDescriptions') : this.translate('addDescriptions')}
                              </PuzzleButton>
                            </>
                          :
                          <div className={'tp-star-container'}>
                            <PuzzleCheckbox
                              titleText={this.translate('SRL-favoriteSub')}
                              checked={isFavorite.value}
                              className={`tp-checkbox-favorite ${isFavorite.value ? 'qa-fakeClass-checked' : ''}`}
                              aria-label={this.translate('SRL-favoriteSub')}
                              onChange={this.changeFavoriteFlag}
                              tooltipPosition={'right'}
                              withTooltip
                            />
                          </div>
                      }
                    </Field>
                    {
                      isTemplate ?
                        null :
                        <Field
                          fieldMandatory={!this.dueDateIgnoreField}
                          fieldClassName={'tp-date-field'}
                        >
                          {!hideDueDateField && <>
                            <LocalizedDatePicker
                              name='specificDate'
                              onChange={this.dueDateOnChange}
                              min={new Date()}
                              format={this.translateByNamespace('date.datePickerFormat')}
                              formatPlaceholder={getFormattedDate(this.translate)}
                              label={this.translate('dueDate')}
                              id={"dueDate"}
                              required={true}
                              valid={specificDate?.valid !== false}
                              errorTxt={this.translateErrorMessage(specificDate)}
                              withValidation
                              {...specificDateField}
                              disabled={disableDueDate}
                              valueAsSimpleType={true}
                            />
                          </>}
                        </Field>
                    }
                  </FieldWrap>
                  <FieldWrap
                    layout={[4, 8]}
                    withValidation
                  >
                    <Field fieldMandatory>
                      <FilterCombo
                        label={this.translate('project')}
                        id={"project"}
                        data={projectData}
                        name='project'
                        textField='name'
                        aria-label={'SelectProject'}
                        valueField="projectId"
                        required={true}
                        onChange={this.handleProjectChange}
                        onFocus={this.projectOnFocus}
                        handleFormChange={this.props.form.handleChange}
                        disabled={this.state.isProjectDisabled}
                        translateMethod={this.translate.bind(this)}
                        errorTxt={this.translateErrorMessage(project)}
                        valueAsSimpleType={true}
                        withValidation
                        {...project}
                      />
                    </Field>
                    <Field fieldMandatory>
                      <PuzzleTagField
                        aria-label={'SelectProjectManager'}
                        autoClose={false}
                        data={orderBy(pmData, [{
                          field: 'fullName',
                          dir: 'asc'
                        }])}
                        disabled={completedSubmission}
                        errorTxt={this.translateErrorMessage(projectManager)}
                        id={"projectManager"}
                        label={this.translate('projectManager')}
                        listNoDataRender={() => noDataDropDownRender(this.translate('emptyProjectManager'))}
                        name='projectManager'
                        onChange={handleChange}
                        required={true}
                        textField={'fullName'}
                        valueField={'userId'}
                        withValidation
                        {...projectManager}
                      />
                    </Field>
                  </FieldWrap>
                  {(this.state.showClaim && mslaLevelData?.length) ?
                    this.workflowWithMslaAndClaimFieldsRender() : this.workflowRender()
                  }
                </form>) : this.tptModeRender()}
              </Container>
              <Container
                className={'tp-details-second-section'}
                hBox
              >
                <Container
                  ariaLabel={this.translate('SRL-sectionLang')}
                  className={'tp-details-second-section-languages'}
                  htmlTag={'section'}
                  tabIndex={0}
                >
                  <Container hBox>
                    <Container
                      htmlTag={'h3'}
                      hBox
                      alignItemsCenter
                      justifyContentBetween
                      className="tp-section-container-title tp-font-size-md"
                    >
                      {this.translate('languagesTitle')}
                    </Container>
                    <PuzzleButton
                      titleText={this.translate('SRL-openLangSection')}
                      aria-label={this.translate('SRL-openLangSection')}
                      iconClass={'tp-icon tp-open_in_new'}
                      className={project.value ? '' : 'k-state-disabled'}
                      disabled={!project.value}
                      fillMode='flat'
                      themeColor={'primary'}
                      onClick={() => {
                        this.dispatch('openSubmissionPopup', '', {
                          detailsProps: this.props,
                          popupName: 'languages',
                          detailsState: this.state,
                          submissionMode
                        })
                      }}
                      tooltipPosition={'right'}
                      withTooltip
                    />
                  </Container>
                  <form className={'k-form k-vbox'} data-cy={"sourceLanguage"}
                  >
                    <FieldWrap withValidation>
                      <Field fieldMandatory>
                        <FilterCombo
                          label={this.translate('sourceLanguage')}
                          id={"sourceLanguage"}
                          name="sourceLanguage"
                          data={languageDirectionData}
                          textField='sourceLanguageDisplayName'
                          onChange={this.onSourceLanguageChange}
                          valueField='source'
                          required={true}
                          handleFormChange={this.props.form.handleChange}
                          disabled={startedSubmission}
                          listNoDataRender={() => noDataDropDownRender(this.translate(`emptySourceLanguage${tptEnabled ? 'Tpt' : ''}`))}
                          translateMethod={this.translate.bind(this)}
                          errorTxt={this.translateErrorMessage(sourceLanguage)}
                          valueAsSimpleType={true}
                          withValidation
                          {...sourceLanguage}
                        />
                      </Field>
                    </FieldWrap>
                    {!isMultiBatch?.value ? (
                      <FieldWrap withValidation>
                        <Field fieldMandatory>
                          <PuzzleTagField
                            autoClose={false}
                            className={'tp-target-lang-tagfield'}
                            data={targetLanguageData}
                            errorTxt={this.translateErrorMessage(targetLanguage)}
                            id={'targetLanguage'}
                            itemColoring={targetLanguageItemColoring.bind(this)}
                            label={this.translate('targetLanguage')}
                            markInRed={true} disabled={this.isTargetLanguageDisabled() || startedSubmission}
                            name="targetLanguage"
                            onChange={this.handleTargetLanguageChange}
                            onOpen={preventDisabledTagToOpen}
                            required={true}
                            showCount
                            tagRender={(data: TM_DATA, li: ReactElement<any>) => tmTagRenderer(data, li, this.dispatch.bind(this), project.value, paClient.value)}
                            textField='targetLanguageDisplayName'
                            valueField='targetLanguage'
                            withValidation
                            {...targetLanguage}
                          />
                        </Field>
                      </FieldWrap>) : <div
                      className="tp-target-languages-placeholder k-hbox k-align-items-center k-justify-content-center k-flex">
                      <div className="k-vbox k-justify-content-center">
                        <i className="tp-icon tp-info tp-icon-l tp-color-accent" aria-hidden="true" />
                        <span className={'k-text-center'}>{this.translate('targetLanguagePlaceholderText')}</span>
                      </div>
                    </div>}
                  </form>
                </Container>
                <Container
                  ariaLabel={this.translate('SRL-sectionInstructions')}
                  className={'tp-details-second-section-instructions'}
                  htmlTag={'section'}
                  tabIndex={0}
                >
                  <Instructions
                    ref={component => this.instructionsRef = component}
                    form={form}
                    submissionStatus={submissionStatus}
                  />
                </Container>
              </Container>
            </Container>
          </Container>
        </Container>
        <PuzzleTooltip
          ref={(el: any) => this.tooltipRef = el}
          anchorElement="target"
          openDelay={1000}
          position="bottom"
        />
      </Container>
    );
  }
}

export default Details;
